/**
 * Post-authentication redirect service.
 * Determines where to redirect users after successful login on login.cnvrgnx.com
 * 
 * Uses magic link token transfer for cross-domain session establishment.
 */

import { supabase } from '@/integrations/supabase/client';
import { PLATFORM_URL } from '@/config/auth';

interface RedirectResult {
  /** Full URL to redirect to (includes /accounts/session-transfer path) */
  url: string;
  /** The subdomain being redirected to */
  subdomain: string | null;
  /** Whether this is a platform admin user */
  isPlatformAdmin: boolean;
}

/**
 * Determine the target URL for post-authentication redirect.
 * 
 * Priority:
 * 1. redirect query parameter (if valid cnvrgnx.com subdomain)
 * 2. User's organization subdomain
 * 3. Platform (fallback for platform admins or users without org)
 */
export async function getPostAuthRedirectUrl(
  userId: string,
  searchParams: URLSearchParams
): Promise<RedirectResult> {
  const platformUrl = import.meta.env.VITE_PLATFORM_URL || PLATFORM_URL || 'https://platform.cnvrgnx.com';
  
  // Check for redirect parameter first
  const redirectParam = searchParams.get('redirect');
  if (redirectParam) {
    try {
      const redirectUrl = new URL(decodeURIComponent(redirectParam));
      // Validate it's a valid workspace subdomain (not login, marketing, or platform infrastructure)
      if (redirectUrl.hostname.endsWith('.cnvrgnx.com') && 
          redirectUrl.hostname !== 'cnvrgnx.com') {
        const subdomain = redirectUrl.hostname.replace('.cnvrgnx.com', '');
        
        // Exclude non-workspace subdomains (marketing, auth infrastructure, login)
        const nonWorkspaceSubdomains = ['login', 'www', 'auth', 'scim', 'api', 'app', 'admin'];
        
        if (subdomain && !nonWorkspaceSubdomains.includes(subdomain)) {
          return {
            url: `https://${subdomain}.cnvrgnx.com/accounts/session-transfer`,
            subdomain,
            isPlatformAdmin: subdomain === 'platform'
          };
        }
      }
      // Invalid redirect domain - log and fall through to org lookup
      console.warn('[authRedirect] Invalid redirect domain, using org lookup:', redirectParam);
    } catch (e) {
      console.warn('[authRedirect] Invalid redirect URL:', redirectParam);
    }
  }
  
  // Lookup user's organization
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('organization_id')
    .eq('id', userId)
    .single();

  if (profileError || !profile?.organization_id) {
    console.log('[authRedirect] No organization found, defaulting to platform');
    return {
      url: `${platformUrl}/accounts/session-transfer`,
      subdomain: 'platform',
      isPlatformAdmin: true
    };
  }

  // Get organization details
  const { data: org, error: orgError } = await supabase
    .from('organizations')
    .select('subdomain, is_platform_admin')
    .eq('id', profile.organization_id)
    .single();

  if (orgError || !org) {
    console.log('[authRedirect] Organization lookup failed, defaulting to platform');
    return {
      url: `${platformUrl}/accounts/session-transfer`,
      subdomain: 'platform',
      isPlatformAdmin: true
    };
  }

  // Platform admin users go to platform
  if (org.is_platform_admin) {
    return {
      url: `${platformUrl}/accounts/session-transfer`,
      subdomain: 'platform',
      isPlatformAdmin: true
    };
  }

  // Tenant users go to their subdomain
  if (org.subdomain) {
    return {
      url: `https://${org.subdomain}.cnvrgnx.com/accounts/session-transfer`,
      subdomain: org.subdomain,
      isPlatformAdmin: false
    };
  }

  // Fallback to platform
  return {
    url: `${platformUrl}/accounts/session-transfer`,
    subdomain: 'platform',
    isPlatformAdmin: true
  };
}
